import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  assessAuthenticity,
  BACKGROUND_SIGNAL_IDS,
  BACKGROUND_WEIGHTS,
  backgroundBand,
  confidenceWord,
  DISCLAIMER,
  judgeRisk,
} from '../authenticity/index.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * «Замена фона».
 *
 * Uses the boundary/halo signal (F), the skin-vs-background noise mismatch (G)
 * and the resampling signal (C), enriched with the background metrics already
 * computed for the frame (uniformity, colour spread, silhouette box).
 *
 * This is the only authenticity check allowed to attach an overlay marker, and
 * only over the background band — the validator can point at the background
 * honestly, but it cannot localise retouching on a face.
 */
export const backgroundReplacementCheck: ValidationCheck = {
  id: 'background_replacement',
  title: 'Замена фона',
  description:
    'Ищет ровный «вырезанный» контур, ореол вдоль силуэта и несовпадение шума и однородности фона с субъектом.',
  kind: 'content',
  requires: ['pixels', 'background'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'background']);
    if (missing) return notChecked(ctx, backgroundReplacementCheck, missing, ctx.rule.severity);
    const background = ctx.background;
    if (!background) {
      return notChecked(
        ctx,
        backgroundReplacementCheck,
        'Метрики фона недоступны.',
        ctx.rule.severity,
      );
    }

    const assessment = assessAuthenticity(ctx, {
      signalIds: BACKGROUND_SIGNAL_IDS,
      weights: BACKGROUND_WEIGHTS,
    });
    const warnRisk = numberParam(ctx.rule.params, 'warnRisk', 0.4);
    const uncertainBelowConfidence = numberParam(
      ctx.rule.params,
      'uncertainBelowConfidence',
      0.45,
    );
    const risk = assessment.risk;
    const confidence = assessment.confidence;
    // Background replacement is a suspicion, never a hard failure by default.
    const verdict = judgeRisk({
      risk,
      confidence,
      warnRisk,
      uncertainBelowConfidence,
      failConfidence: 1,
    });
    const status = verdict.status === 'fail' ? 'warning' : verdict.status;
    const word = confidenceWord(confidence);

    /**
     * A background that is both extremely uniform and colour-flat while a
     * pixel-level signal fires is the concrete, explainable core of this check.
     */
    const findings = [...assessment.findings];
    const colourSpread = Math.max(
      background.meanColor.r,
      background.meanColor.g,
      background.meanColor.b,
    ) - Math.min(background.meanColor.r, background.meanColor.g, background.meanColor.b);
    const mismatchFired = assessment.signals.some(
      (signal) => signal.id === 'noise_mismatch' && signal.score >= 0.3,
    );
    if (mismatchFired && background.uniformity >= 0.8 && colourSpread <= 12) {
      findings.unshift('фон почти идеально однороден по цвету и яркости при текстурированном субъекте');
    }
    const listedFindings = findings.length > 0 ? findings : ['явных признаков не обнаружено'];

    const message =
      status === 'pass'
        ? '✅ Признаков замены фона не обнаружено'
        : status === 'warning'
          ? '⚠ Возможна замена/генерация фона'
          : 'Не удалось уверенно проверить фон на признаки замены.';


    const subject = ctx.geometry?.headBox ?? background.subjectRect;
    const band = backgroundBand(subject);

    return plainResult({
      id: backgroundReplacementCheck.id,
      title: backgroundReplacementCheck.title,
      status,
      severity: ctx.rule.severity,
      message,
      fixability: 'retake',
      confidence: roundTo(confidence, 2),
      ...(status === 'warning'
        ? {
            problemRegions: [
              {
                id: 'background_replacement:background_band',
                rect: {
                  x: roundTo(band.x, 4),
                  y: roundTo(band.y, 4),
                  width: roundTo(band.width, 4),
                  height: roundTo(band.height, 4),
                },
                message:
                  'Фон выглядит заменённым или сгенерированным — проверьте фон на фотографии.',
                severity: 'warning' as const,
              },
            ],
          }
        : {}),
      details: {
        risk: roundTo(risk, 4),
        confidence: roundTo(confidence, 4),
        confidenceWord: word,
        signals: assessment.signals.map((signal) => ({
          id: signal.id,
          score: roundTo(signal.score, 4),
          confidence: roundTo(signal.confidence, 4),
          note: signal.note,
        })),
        findings: listedFindings,
        unavailable: assessment.unavailable,
        weights: { ...BACKGROUND_WEIGHTS },
        thresholds: { warnRisk, uncertainBelowConfidence, failRisk: null, failConfidence: null },
        verdictReason: verdict.reason,
        background: {
          uniformity: roundTo(background.uniformity, 4),
          edgeDensity: roundTo(background.edgeDensity, 4),
          colourSpread: roundTo(colourSpread, 2),
          cornerLumaSpread: roundTo(background.cornerLumaSpread, 2),
          meanColor: background.meanColor,
          hasMask: background.hasMask,
        },
        disclaimer: DISCLAIMER,
      },
    });
  },
};
